import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MapPin, Users, ExternalLink, Building2, Edit, Save, X } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import Navbar from "@/components/navbar";
import { Pagination } from "@/components/pagination";
import type { PaginatedResult } from "@shared/schema";

type Venue = {
  id: number;
  name: string;
  vibeVenueUuid: string | null;
  imageUrl: string | null;
  url: string | null;
  capacity: number | null;
  streetAddress: string | null;
  city: string;
  state: string;
  zipCode: string | null;
  market: string | null;
  country: string;
  // Drizzle decimal columns often arrive as strings; handle both
  lat: number | string | null;
  lng: number | string | null;
  googlePlaceId: string | null;
  createdAt: Date;
};

const venueSchema = z.object({
  name: z.string().min(1, "Name is required"),
  vibeVenueUuid: z.string().optional(),
  imageUrl: z.string().url().optional().or(z.literal("")),
  url: z.string().url().optional().or(z.literal("")),
  capacity: z.preprocess((val) => {
    if (val === "" || val === null || val === undefined) return undefined;
    if (typeof val === "string") {
      const n = parseInt(val, 10);
      return Number.isNaN(n) ? undefined : n;
    }
    return val;
  }, z.number().int().positive("Capacity must be positive").optional()),
  streetAddress: z.string().optional(),
  city: z.string().min(1, "City is required"),
  state: z.string().min(1, "State is required"),
  zipCode: z.string().optional(),
  market: z.string().optional(),
  country: z.string().min(1, "Country is required"),
  lat: z.preprocess((val) => {
    if (val === "" || val === null || val === undefined) return undefined;
    if (typeof val === "string") {
      const n = parseFloat(val);
      return Number.isNaN(n) ? undefined : n;
    }
    return val;
  }, z.number().optional()),
  lng: z.preprocess((val) => {
    if (val === "" || val === null || val === undefined) return undefined;
    if (typeof val === "string") {
      const n = parseFloat(val);
      return Number.isNaN(n) ? undefined : n;
    }
    return val;
  }, z.number().optional()),
  googlePlaceId: z.string().optional(),
});

type VenueFormData = z.infer<typeof venueSchema>;

interface VenueCardProps {
  venue: Venue;
  onEdit: (venue: Venue) => void;
}

function VenueCard({ venue, onEdit }: VenueCardProps) {
  const defaultImage = "https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80";
  
  return (
    <Card className="h-full hover:shadow-lg transition-shadow">
      <div className="aspect-video overflow-hidden rounded-t-lg">
        <img 
          src={venue.imageUrl || defaultImage} 
          alt={venue.name}
          className="w-full h-full object-cover"
        />
      </div>
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <h3 className="text-xl font-bold text-neutral-900 mb-2">
              {venue.url ? (
                <a 
                  href={venue.url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-primary hover:underline flex items-center gap-1"
                >
                  {venue.name}
                  <ExternalLink className="w-4 h-4" />
                </a>
              ) : (
                venue.name
              )}
            </h3>
            
            <div className="space-y-2">
              <div className="flex items-center text-sm text-neutral-600">
                <Users className="w-4 h-4 mr-2" />
                <span>{venue.capacity != null ? `${venue.capacity.toLocaleString()} capacity` : "Capacity unknown"}</span>
              </div>
              
              <div className="flex items-center text-sm text-neutral-600">
                <MapPin className="w-4 h-4 mr-2" />
                <div>
                  {venue.streetAddress && (
                    <div>{venue.streetAddress}</div>
                  )}
                  <div>
                    {venue.city}, {venue.state} {venue.zipCode}
                  </div>
                  <div>{venue.country}</div>
                </div>
              </div>

              {venue.market && (
                <div className="flex items-center text-sm text-neutral-600">
                  <Building2 className="w-4 h-4 mr-2" />
                  <span>Market: {venue.market}</span>
                </div>
              )}
            </div>
          </div>
          
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => onEdit(venue)}
            className="ml-4"
          >
            <Edit className="w-4 h-4 mr-1" />
            Edit
          </Button>
        </div>

        {venue.vibeVenueUuid && (
          <Badge variant="secondary" className="mt-2">
            VIBE UUID: {venue.vibeVenueUuid}
          </Badge>
        )}
      </CardContent>
    </Card>
  );
}

export default function AdminVenues() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedState, setSelectedState] = useState("");
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingVenue, setEditingVenue] = useState<Venue | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 60;

  const { data: paginated, isLoading } = useQuery<PaginatedResult<Venue>>({
    queryKey: ['/api/venues/paginated', { q: searchTerm, state: selectedState || undefined, page: currentPage, limit: itemsPerPage }],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (searchTerm) params.set('q', searchTerm);
      if (selectedState && selectedState !== 'all') params.set('state', selectedState);
      params.set('page', String(currentPage));
      params.set('limit', String(itemsPerPage));
      const res = await fetch(`/api/venues/paginated?${params.toString()}`);
      if (!res.ok) throw new Error('Failed to load venues');
      return res.json();
    }
  });

  // Reset to first page when filters change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setCurrentPage(1); }, [searchTerm, selectedState]);

  const form = useForm<VenueFormData>({
    resolver: zodResolver(venueSchema),
    defaultValues: {
      name: "",
      vibeVenueUuid: "",
      imageUrl: "",
      url: "",
      capacity: undefined,
      streetAddress: "",
      city: "",
      state: "",
      zipCode: "",
      market: "",
      country: "USA",
      lat: undefined,
      lng: undefined,
      googlePlaceId: "",
    },
  });

  const updateVenueMutation = useMutation({
    mutationFn: (data: { id: number; venue: VenueFormData }) => 
      apiRequest(`/api/venues/${data.id}`, { method: 'PUT', body: data.venue }),
    onSuccess: (updatedVenue: Venue, variables) => {
      // Directly patch cached paginated data with the server response
      // so the UI updates immediately (avoids browser HTTP cache serving stale data)
      queryClient.setQueriesData<PaginatedResult<Venue>>(
        { queryKey: ['/api/venues/paginated'] },
        (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            items: oldData.items.map((v) =>
              v.id === variables.id ? { ...v, ...updatedVenue } : v
            ),
          };
        }
      );
      setIsEditDialogOpen(false);
      setEditingVenue(null);
      form.reset();
      toast({
        title: "Venue updated",
        description: "The venue has been updated successfully.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error updating venue",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleEditVenue = (venue: Venue) => {
    setEditingVenue(venue);
    form.reset({
      name: venue.name,
      vibeVenueUuid: venue.vibeVenueUuid || "",
      imageUrl: venue.imageUrl || "",
      url: venue.url || "",
      capacity: venue.capacity ?? undefined,
      streetAddress: venue.streetAddress || "",
      city: venue.city,
      state: venue.state,
      zipCode: venue.zipCode || "",
      market: venue.market || "",
      country: venue.country,
      lat: venue.lat != null && venue.lat !== "" && !Number.isNaN(Number(venue.lat)) ? Number(venue.lat) : undefined,
      lng: venue.lng != null && venue.lng !== "" && !Number.isNaN(Number(venue.lng)) ? Number(venue.lng) : undefined,
      googlePlaceId: venue.googlePlaceId || "",
    });
    setIsEditDialogOpen(true);
  };

  const handleSubmit = (data: VenueFormData) => {
    if (!editingVenue) return;
    
    const toNullIfEmpty = (v?: string) => (v && v.trim() !== "" ? v : null);

    const cleanData = {
      ...data,
      // Convert cleared fields to null so DB updates correctly
      vibeVenueUuid: toNullIfEmpty(data.vibeVenueUuid),
      imageUrl: toNullIfEmpty(data.imageUrl),
      url: toNullIfEmpty(data.url),
      streetAddress: toNullIfEmpty(data.streetAddress),
      zipCode: toNullIfEmpty(data.zipCode),
      market: toNullIfEmpty(data.market),
      googlePlaceId: toNullIfEmpty(data.googlePlaceId),
      // Numbers: set null when cleared
      capacity: data.capacity ?? null,
      lat: data.lat ?? null,
      lng: data.lng ?? null,
    };

    updateVenueMutation.mutate({ id: editingVenue.id, venue: cleanData as any });
  };

  const venues = paginated?.items ?? [];
  const totalItems = paginated?.totalItems ?? 0;
  const totalPages = paginated?.totalPages ?? 0;
  // Build state options from current page items (lightweight)
  const states = [...new Set(venues.map(venue => venue.state).filter(state => state && state.trim() !== ""))].sort();

  if (!user || (user.role !== 'admin' && user.role !== 'super_admin' && user.role !== 'crm_user')) {
    return (
      <div className="min-h-screen bg-neutral-50">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-neutral-900 mb-4">Access Denied</h1>
            <p className="text-neutral-600">You don't have permission to access this page.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-neutral-900">Venue Management</h1>
            <p className="text-neutral-600 mt-2">
              Manage and edit venue information
            </p>
          </div>
          <Badge variant="outline" className="text-sm">
            {totalItems} venues
          </Badge>
        </div>

        {/* Search and Filter */}
        <div className="mb-6 flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <Input
              placeholder="Search venues by name, city, or state..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full"
            />
          </div>
          
          <div className="sm:w-48">
            <Select value={selectedState} onValueChange={setSelectedState}>
              <SelectTrigger>
                <SelectValue placeholder="Filter by state" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All states</SelectItem>
                {states.map(state => (
                  <SelectItem key={state} value={state}>{state}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Venues Grid */}
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <Card key={i} className="animate-pulse">
                <div className="aspect-video bg-gray-200 rounded-t-lg"></div>
                <CardContent className="p-6">
                  <div className="h-4 bg-gray-200 rounded mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-3/4 mb-4"></div>
                  <div className="h-10 bg-gray-200 rounded"></div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : venues.length === 0 ? (
          <div className="text-center py-12">
            <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-lg font-semibold text-gray-600">No venues found</p>
            <p className="text-sm text-gray-500">Try adjusting your search filters</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {venues.map((venue) => (
              <VenueCard
                key={venue.id}
                venue={venue}
                onEdit={handleEditVenue}
              />
            ))}
          </div>
        )}

        {/* Pagination */}
        <Pagination
          className="mt-8"
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={(p) => { setCurrentPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
        />

        {/* Edit Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Venue</DialogTitle>
            </DialogHeader>
            
            <Form {...form}>
              <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Name *</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="vibeVenueUuid"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Venue ID</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="imageUrl"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Image URL</FormLabel>
                        <FormControl>
                          <Input {...field} type="url" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="url"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Website URL</FormLabel>
                        <FormControl>
                          <Input {...field} type="url" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="capacity"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Capacity</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          value={field.value ?? ""}
                          onChange={(e) => {
                            const v = e.target.value;
                            if (v === "") {
                              field.onChange(undefined);
                            } else {
                              const n = parseInt(v, 10);
                              field.onChange(Number.isNaN(n) ? undefined : n);
                            }
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="streetAddress"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Street Address</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <FormField
                    control={form.control}
                    name="city"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>City *</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="state"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>State *</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="zipCode"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>ZIP Code</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  
                  <FormField
                    control={form.control}
                    name="country"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Country *</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="lat"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Latitude</FormLabel>
                        <FormControl>
                          <Input 
                            {...field} 
                            type="number" 
                            step="any"
                            onChange={(e) => field.onChange(parseFloat(e.target.value) || undefined)}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="lng"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Longitude</FormLabel>
                        <FormControl>
                          <Input 
                            {...field} 
                            type="number" 
                            step="any"
                            onChange={(e) => field.onChange(parseFloat(e.target.value) || undefined)}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="googlePlaceId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Google Place ID</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="flex gap-2 pt-4">
                  <Button 
                    type="submit" 
                    disabled={updateVenueMutation.isPending}
                    className="flex-1"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {updateVenueMutation.isPending ? "Saving..." : "Save Changes"}
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setIsEditDialogOpen(false)}
                    className="flex-1"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Cancel
                  </Button>
                </div>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
